<template>
  <div class="vz-wrapping-group">
    <div class="flex flex-align-items-center">
      <vz-checkbox
        :modelValue="status"
        :label="group.name"
        :small="false"
        :disabled="disabled"
        :title="disabled || undefined"
        @change="changeOnGroup"
      />
      <vz-tooltip-info
        v-if="hasGroupTooltip"
        class="vz-wrapping-group-tooltip"
      >
        <template #baloon>
          <div class="flex flex-align-items-start">
            <img
              v-if="getTooltipImg(group)"
              :src="getTooltipImg(group)"
              class="mr-10"
              alt=""
            >
            <span v-html="groupTooltip" />
          </div>
          <a v-if="groupTooltipUrl" :href="groupTooltipUrl" target="_blank" class="flex flex-end text-decoration-underline mt-5">Подробнее</a>
        </template>
      </vz-tooltip-info>
    </div>
    <div v-if="showGroupItems" class="ml-15 mt-10">
      <div v-if="groupItems.length > 0">
        <vz-radiogroup
          v-if="isGroupWithVolumeItems"
          :items="groupItems"
          :modelValue="itemValue"
          with-tooltips
          big
          @change="changeOnItem"
        />
        <div v-else class="flex flex-align-items-end">
          <vz-select
            :modelValue="itemValue"
            :items="groupItems"
            label="Габариты"
            class="vz-wrapping-group-select"
            @change="changeOnItem"
          />
          <vz-measure-input
            :modelValue="countValue"
            label="Кол-во"
            class="vz-wrapping-group-input"
            placeholder="1"
            measure="шт"
            :max-value="999"
            :min-value="1"
            :mask-decimal-limit="0"
            with-max-notice
            @update:modelValue="changeOnItemCount"
          />
        </div>
      </div>
      <div v-if="isGroupNumeric">
        <vz-measure-input
          :modelValue="countValue"
          label="Кол-во"
          class="vz-wrapping-group-input"
          placeholder="1"
          measure="шт"
          :max-value="999"
          :min-value="1"
          :mask-decimal-limit="0"
          with-max-notice
          @update:modelValue="changeOnItemCount"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {PropType} from 'vue';
import {OrderWrappingItem, OrderWrappingType, OrderWrappingTypeItem, VzRadioGroupItem} from '~/types/types';
import {useOrderStore} from '~/stores/order';
import VzSelect from '~/components/ui-kit/vz-select.vue';
import useCargoBlock from '~/components/personal/order/cargo/useCargoBlock';
const wrappingTooltipIcons = (await import('~/common/objects/wrappingTooltipIcons')).default;

const emit = defineEmits(['change'])

const props = defineProps({
  item: {
    type: Object as PropType<OrderWrappingType>,
    default: () => ({})
  },
  wrapping: {
    type: Array as PropType<(OrderWrappingItem&string)[]>,
    default: () => ({})
  }
})

const orderStore = useOrderStore()
const { isParcelsMode } = useCargoBlock()

const status = ref(false)
const itemValue = ref('')
const countValue = ref(1)
const showGroupItems = ref(false)

const group = computed((): OrderWrappingType | OrderWrappingTypeItem => {
  return props.item.id ? props.item : props.item.list[0]
})

// now is only for safePackage
const isGroupNumeric = computed(() => {
  return (group.value as OrderWrappingTypeItem).type === 'numeric'
})

const disabled = computed(() => {
  if (status.value) {
    // Так как блокировка ставится только на активность, то если уже активно - можно деактивировать.
    // Например установили упаковку, потом выставили ПВЗ и данная упаковка заблокирована.
    // При активной упаковке будет ошибка с бэка, чтобы пользователь сам снял упаковку
    return false
  }
  if (props.item.id && groupItems.value.find(item => !item.disabled)) {
    return false
  }
  return checkItemDisabled(props.item.list[0])
})

const groupTooltip = computed(() => {
  let tooltip = ''
  if (!props.item.id) {
    tooltip = props.item.list[0].description
  }
  if (group.value.code === 'box' || group.value.code === 'bag') {
    tooltip = props.item.list.find(i => i.code === itemValue.value)?.description || ''
  }
  if (tooltip && orderStore.orderType === 'networking') {
    tooltip += ' Разбирается на филиале и не выдаётся.'
  }

  return tooltip
})

const hasGroupTooltip = computed(() => {
  return groupTooltip.value.length > 0
})

const isNetwork = computed(() => {
  return orderStore.orderForm.isNetwork || false
})
const isPup = computed(() => {
  return orderStore.orderForm.directions.destination.direction === 'pickupPoint'
})

const groupItems = computed((): VzRadioGroupItem[] => {
  const items = [] as VzRadioGroupItem[]
  if ((group.value as OrderWrappingType).list) {
    (group.value as OrderWrappingType).list.forEach((element) => {
      const demountable = orderStore.orderType === 'networking' &&
        !element.code.includes('extraPackage') &&
        element.code !== 'hardPackageVolume_WP' &&
        element.code !== 'hardPackageVolume'
      items.push({
        title: element.name,
        value: element.code,
        tooltip: element.description + (demountable ? ' Разбирается на филиале и не выдаётся.' : ''),
        tooltipImg: getTooltipImg(element),
        url: (group.value.code === 'box' || group.value.code === 'bag') ? '' : element.url,
        disabled: checkItemDisabled(element)
      })
    })
  }
  return items
})

const isGroupWithVolumeItems = computed(() => {
  return props.item.list && props.item.list[0]?.type === 'volume'
})

const groupTooltipUrl = computed(() => {
  if (group.value.code === 'box' || group.value.code === 'bag') {
    return props.item.list.find(i => i.code === itemValue.value)?.url || ''
  }
  return group.value.url || ''
})

function getTooltipImg(wrapping: OrderWrappingType | OrderWrappingTypeItem): string | undefined {
  const image = (wrapping.code === 'box' || wrapping.code === 'bag')
    ? props.item.list.find(i => i.code === itemValue.value)?.image
    : wrapping.image
  if (image || !wrapping.code) {
    return image || undefined
  }
  const icon = wrappingTooltipIcons[wrapping.code]
  return icon ? `/upload/wrapping/${icon}` : undefined
}

function resetToInitial(): void {
  status.value = false
  itemValue.value = ''
  countValue.value = 1
  showGroupItems.value = false
}

function changeOnItem(item: VzRadioGroupItem): void {
  emit('change', false, itemValue.value)
  itemValue.value = String(item.value)
  emit('change', true, item.value)
}

function changeOnItemCount(count: number): void {
  countValue.value = count
  if (isGroupNumeric.value) {
    emit('change', false, group.value.code, group.value.code)
    emit('change', true, group.value.code, group.value.code, countValue.value)
  } else {
    emit('change', false, itemValue.value, group.value.code)
    emit('change', true, itemValue.value, group.value.code, countValue.value)
  }
}

function changeOnGroup(): void {
  status.value = !status.value
  if ((group.value as OrderWrappingType).list) {
    if (status.value) {
      itemValue.value = String(groupItems.value[0].value)
      emit('change', true, (group.value as OrderWrappingType).list[0].code, group.value.code)
    } else {
      emit('change', false, itemValue.value, group.value.code)
      itemValue.value = ''
    }
    showGroupItems.value = !showGroupItems.value
  } else if (isGroupNumeric.value) {
    emit('change', status.value, group.value.code, group.value.code)
    showGroupItems.value = !showGroupItems.value
  } else {
    emit('change', status.value, group.value.code, group.value.code)
  }
}

/** @return string(reason)|false */
function checkItemDisabled(item: OrderWrappingTypeItem) {
  if (!item.pup && isPup.value) {
    return orderStore.orderCreateContent?.pickupPointInfo.blockedHint
  }
  if (!item.network && isNetwork.value) {
    return 'Недоступно для торговых сетей'
  }
  if (!item.parcel && isParcelsMode.value) {
    return 'Недоступно в посылках'
  }
  return false
}

watch(() => props.wrapping,
  () => {
    resetToInitial()
    const groupData = props.wrapping.find((item) => {
      return item.code === group.value.code || item === group.value.code
    })
    if (groupItems.value.length === 0 && status.value !== Boolean(groupData)) {
      status.value = Boolean(groupData)
      if (status.value && isGroupNumeric.value) {
        showGroupItems.value = true
        countValue.value = Number(groupData?.value || 1)
      }
    }
    const itemData = props.wrapping.find((item) => {
      return groupItems.value.find((element) => {
        return element.value === item.code
      })
    })

    if (itemData && (itemValue.value !== String(itemData?.code))) {
      status.value = true
      showGroupItems.value = true
      itemValue.value = String(itemData?.code)
      countValue.value = Number(itemData?.value)
    }
  },
  { immediate: true }
)
</script>
